import React from "react";
import { Route, Redirect } from "react-router-dom";
import { useSelector } from "react-redux";

// Role-aware protected route.
// Uses Redux auth state (with sessionStorage fallback) to require both authentication and role.
export const ProtectedRoute = ({
  component: Component,
  ...rest
}) => {
  // patient-only pages whitelist
  const patientPages = [
    "/overview_of_journey",
    "/patient_feedback",
    "/patient_messages",
    "/physiotherapeutic_plan",
    "/learn_about_your_pain",
    "/myaccount",
    "/recovery_phases",
    "/my_appointments",
    "/portal_pending_activation",
    "/FollowUpAssessment",
    "/FollowUpAssessment_questionaire"
  ];

  // Prefer Redux state; fall back to sessionStorage for page reloads
  const reduxIsLoggedIn = useSelector(state => state && state.auth && state.auth.isLoggedIn);
  const reduxRole = useSelector(state => state && state.auth && state.auth.role);
  const reduxUser = useSelector(state => state && state.auth && state.auth.user);

  const isLoggedIn = (typeof reduxIsLoggedIn !== 'undefined' && reduxIsLoggedIn !== null) ? reduxIsLoggedIn : (sessionStorage.getItem("isLoggedIn") === 'true');
  const role = reduxRole ? reduxRole : (sessionStorage.getItem("role") ? JSON.parse(sessionStorage.getItem("role")) : null);
  const user = reduxUser ? reduxUser : (sessionStorage.getItem("user") ? JSON.parse(sessionStorage.getItem("user")) : null);

  // Use strict role_type checks to avoid accidental substring matches.
  // Backend normalizes role_type to canonical values such as 'patient', 'clinic_admin', 'careprovider_admin', 'care_staff', etc.
  // Determine patient/care provider using role when available, otherwise fall back to user flags.
  const isClinicSideRole = role && [
    'careprovider_admin',
    'care_provider',
    'clinic_admin',
    'care_staff',
    'admin',
    'physiotherapist'
  ].includes(role.role_type);
  const isPatient = (role && role.role_type === 'patient') || Boolean(
    user && user.has_profile === true && !user.is_staff && !user.admin && !user.has_care_provider
  );
  // Treat clinic_admin and care_staff (and other non-patient roles) as care-provider/admin users
  const careProviderTypes = [
    'careprovider_admin',
    'care_provider',
    'clinic_admin',
    'care_staff',
    'admin',
    'physiotherapist'
  ];
  const isCareProvider = (role && careProviderTypes.includes(role.role_type)) || (user && (user.is_staff === true || user.admin === true || user.has_care_provider === true));

  return (
    <Route
      {...rest}
      render={props => {
        // Debug log: show current auth/role/user state for troubleshooting redirects
        const pathDebug = props.location && props.location.pathname ? props.location.pathname : 'unknown';
        console.debug('[ProtectedRoute] render', { isLoggedIn, role, user, path: pathDebug });

        // Must be logged in. If role is missing, fall back to user flags.
        if (!isLoggedIn) {
          const requestedPath = `${props.location.pathname}${props.location.search || ''}${props.location.hash || ''}`;
          return (
            <Redirect
              to={{
                pathname: "/",
                search: `?redirect=${encodeURIComponent(requestedPath)}`,
                state: {
                  from: props.location
                }
              }}
            />
          );
        }
  const path = props.location.pathname;

  // If role is missing, compute effective booleans from user flags only
  const effectiveIsPatient = isPatient || (!role && user && user.has_profile === true && !user.is_staff && !user.admin && !user.has_care_provider);
  const effectiveIsCareProvider = isCareProvider || isClinicSideRole || (!role && user && (user.is_staff === true || user.admin === true || user.has_care_provider === true));
  const requiresFirstAttendance = !!(user && user.portal_access_requires_first_attendance);

  // Debug log: computed effective flags
  console.debug('[ProtectedRoute] decision', { path, isPatient, isCareProvider, effectiveIsPatient, effectiveIsCareProvider, requiresFirstAttendance });

        if (effectiveIsPatient && requiresFirstAttendance && path !== "/portal_pending_activation") {
          console.debug('[ProtectedRoute] redirecting locked patient to portal_pending_activation', { path, requiresFirstAttendance });
          return (
            <Redirect
              to={{
                pathname: "/portal_pending_activation",
                state: { from: props.location }
              }}
            />
          );
        }

        if (effectiveIsPatient && !requiresFirstAttendance && path === "/portal_pending_activation") {
          console.debug('[ProtectedRoute] redirecting unlocked patient to physiotherapeutic_plan', { path, requiresFirstAttendance });
          return (
            <Redirect
              to={{
                pathname: "/physiotherapeutic_plan",
                state: { from: props.location }
              }}
            />
          );
        }

        // If user is a patient but tries to access non-patient pages, send them to patient plan
        if (effectiveIsPatient && patientPages.indexOf(path) === -1) {
          console.debug('[ProtectedRoute] redirecting patient to physiotherapeutic_plan', { path, effectiveIsPatient });
          return (
            <Redirect
              to={{
                pathname: "/physiotherapeutic_plan",
                state: { from: props.location }
              }}
            />
          );
        }

        // If user is a care provider and tries to access patient-only pages, send to admin dashboard
        if (effectiveIsCareProvider && patientPages.indexOf(path) !== -1) {
          console.debug('[ProtectedRoute] redirecting care provider to admin_dashboard', { path, effectiveIsCareProvider });
          return (
            <Redirect
              to={{
                pathname: "/admin_dashboard",
                state: { from: props.location }
              }}
            />
          );
        }

        // otherwise allow
        return <Component {...props} />;
      }}
    />
  );
};
